import React, { useState, useCallback, useMemo } from 'react'
import {
  Modal,
  Input,
  Button,
  List,
  Avatar,
  Spin,
  Empty,
  Tag,
  Divider,
  Radio,
  Typography,
  Space,
  message
} from 'antd'
import {
  SearchOutlined,
  UserOutlined,
  ArrowRightOutlined,
  MergeCellsOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import api from '../utils/api'
import Hint from './common/Hint'

const { Text } = Typography

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin: 16px 0 8px;
  color: #333;
`

const CompareRow = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr 1fr;
  gap: 12px;
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  align-items: center;
  font-size: 13px;

  &:last-child {
    border-bottom: none;
  }
`

const FieldLabel = styled.div`
  color: #666;
  font-weight: 500;
`

const ValueCell = styled.div`
  padding: 6px 10px;
  border-radius: 4px;
  background: ${(p) => (p.$selected ? '#fff1f5' : '#fafafa')};
  border: 1px solid ${(p) => (p.$selected ? '#ff9eb1' : '#eee')};
  cursor: pointer;
  word-break: break-all;
  transition: all 0.15s;

  &:hover {
    border-color: #ff9eb1;
  }
`

const ResourceList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 8px;
  margin-top: 8px;
`

const ResourceItem = styled.div`
  padding: 8px 10px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  strong {
    color: #ff3366;
  }
`

const FIELDS = [
  { key: 'name', label: '姓名（平台）' },
  { key: 'phone', label: '電話' },
  { key: 'email', label: 'Email' },
  { key: 'gender', label: '性別' },
  { key: 'birthdate', label: '生日', format: (v) => (v ? String(v).slice(0, 10) : '') },
  { key: 'avatar', label: '頭像', format: (v) => (v ? '✓ 有' : '') }
]

const BUSINESS_FIELDS = [
  { key: 'business.name', label: '店家內姓名', read: (b) => b?.name },
  { key: 'business.phone', label: '店家內電話', read: (b) => b?.phone },
  { key: 'business.email', label: '店家內 Email', read: (b) => b?.email },
  { key: 'business.gender', label: '店家內性別', read: (b) => b?.gender },
  { key: 'business.birthdate', label: '店家內生日', read: (b) => (b?.birthdate ? String(b.birthdate).slice(0, 10) : '') },
  { key: 'business.region', label: '地址', read: (b) => b?.region },
  { key: 'business.membershipLevel', label: '會員等級', read: (b) => b?.membershipLevel }
]

/**
 * 顧客合併 Modal
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - onMerged: () => void  // 成功合併後通知父元件刷新
 *   - businessId: string
 *   - currentCustomer: Customer 物件（被保留方 = target）
 */
const CustomerMergeModal = ({
  open,
  onClose,
  onMerged,
  businessId,
  currentCustomer
}) => {
  const [step, setStep] = useState(1) // 1=搜尋, 2=確認
  const [keyword, setKeyword] = useState('')
  const [searching, setSearching] = useState(false)
  const [results, setResults] = useState([])
  const [selectedSource, setSelectedSource] = useState(null)
  const [preview, setPreview] = useState(null)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [fieldChoices, setFieldChoices] = useState({})
  const [notesStrategy] = useState('concat')
  const [tagsStrategy] = useState('union')
  const [merging, setMerging] = useState(false)

  const reset = useCallback(() => {
    setStep(1)
    setKeyword('')
    setResults([])
    setSelectedSource(null)
    setPreview(null)
    setFieldChoices({})
  }, [])

  const handleClose = useCallback(() => {
    if (merging) return
    reset()
    onClose()
  }, [merging, reset, onClose])

  const doSearch = async () => {
    if (!keyword.trim()) {
      message.warning('請輸入姓名 / 電話 / Email 搜尋')
      return
    }
    setSearching(true)
    try {
      const res = await api.get(
        `/api/business_B/${businessId}/customers/search`,
        { params: { searchTerm: keyword.trim() } }
      )
      const list = (res.data?.data || []).filter(
        (c) => String(c._id) !== String(currentCustomer._id)
      )
      setResults(list)
    } catch (err) {
      message.error('搜尋失敗')
    } finally {
      setSearching(false)
    }
  }

  const pickSource = async (src) => {
    setSelectedSource(src)
    setLoadingPreview(true)
    try {
      const res = await api.get(
        `/api/business_B/${businessId}/customers/${currentCustomer._id}/merge-from/${src._id}/preview`
      )
      setPreview(res.data)
      setStep(2)
    } catch (err) {
      message.error(err.response?.data?.error || '取得合併預覽失敗')
    } finally {
      setLoadingPreview(false)
    }
  }

  const doMerge = async () => {
    setMerging(true)
    try {
      await api.post(
        `/api/business_B/${businessId}/customers/${currentCustomer._id}/merge-from/${selectedSource._id}`,
        { fieldChoices, notesStrategy, tagsStrategy }
      )
      message.success('顧客合併完成')
      reset()
      onClose()
      onMerged?.()
    } catch (err) {
      message.error(err.response?.data?.error || '合併失敗')
    } finally {
      setMerging(false)
    }
  }

  const totalResources = useMemo(() => {
    if (!preview?.resources) return 0
    return Object.values(preview.resources).reduce((a, b) => a + (b || 0), 0)
  }, [preview])

  return (
    <Modal
      title={
        <Space>
          <MergeCellsOutlined />
          <span>合併顧客資料</span>
        </Space>
      }
      open={open}
      onCancel={handleClose}
      width={820}
      footer={
        step === 1 ? (
          <Button onClick={handleClose}>取消</Button>
        ) : (
          <Space>
            <Button onClick={() => setStep(1)} disabled={merging}>
              重新選擇
            </Button>
            <Button onClick={handleClose} disabled={merging}>
              取消
            </Button>
            <Button
              type='primary'
              danger
              loading={merging}
              onClick={() => {
                Modal.confirm({
                  title: '確認合併？',
                  content: (
                    <div>
                      <p>
                        將「<strong>{selectedSource?.name}</strong>」合併到「
                        <strong>{currentCustomer?.name}</strong>」，
                        {preview?.sourceWillBeDeleted
                          ? '來源顧客會被刪除（只關聯這家店）'
                          : '來源顧客仍會保留（還關聯其他店）'}
                        。
                      </p>
                      <p>
                        共有 <strong>{totalResources}</strong>{' '}
                        筆關聯資料會搬過來。此動作無法復原，請確認！
                      </p>
                    </div>
                  ),
                  okText: '確認合併',
                  cancelText: '取消',
                  okButtonProps: { danger: true },
                  onOk: doMerge
                })
              }}
            >
              執行合併
            </Button>
          </Space>
        )
      }
      destroyOnClose
    >
      {step === 1 && (
        <>
          <Hint
            tone='info'
            title='合併規則'
            style={{ marginBottom: 16 }}
          >
            {
              <div style={{ fontSize: 13 }}>
                <p style={{ margin: 0 }}>
                  · 你正在編輯的客戶是「
                  <strong>{currentCustomer?.name}</strong>」 — 合併後會被「
                  <strong>保留</strong>」
                </p>
                <p style={{ margin: '4px 0 0' }}>
                  · 從下方搜尋要併進來的「來源客戶」（將被刪除或解除關聯）
                </p>
                <p style={{ margin: '4px 0 0' }}>
                  · 預約、訂單、儲值、堂票等資料會全部搬移過來
                </p>
              </div>
            }
          </Hint>
          <Input.Search
            placeholder='輸入姓名 / 電話 / Email 搜尋來源客戶'
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onSearch={doSearch}
            enterButton='搜尋'
            loading={searching}
          />
          <div style={{ marginTop: 16 }}>
            {searching ? (
              <div style={{ textAlign: 'center', padding: 24 }}>
                <Spin />
              </div>
            ) : results.length === 0 ? (
              <Empty description='輸入關鍵字搜尋' />
            ) : (
              <List
                dataSource={results}
                renderItem={(item) => (
                  <List.Item
                    actions={[
                      <Button
                        key='pick'
                        type='link'
                        icon={<ArrowRightOutlined />}
                        loading={
                          loadingPreview && selectedSource?._id === item._id
                        }
                        onClick={() => pickSource(item)}
                      >
                        選擇
                      </Button>
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<Avatar icon={<UserOutlined />} />}
                      title={
                        <Space>
                          <span>{item.name || '(未命名)'}</span>
                          {item.platformData?.lineId && (
                            <Tag color='green'>LINE</Tag>
                          )}
                        </Space>
                      }
                      description={
                        <Space size='small'>
                          {item.phone && <span>📱 {item.phone}</span>}
                          {item.email && <span>✉️ {item.email}</span>}
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </div>
        </>
      )}

      {step === 2 && preview && (
        <>
          <Hint
            tone='warning'
            title={
              preview.sourceWillBeDeleted
                ? '來源顧客會被刪除（只關聯這家店）'
                : '來源顧客只移除本店關聯，跨店資料保留'
            }
            style={{ marginBottom: 16 }}
          >
            {
              <div style={{ fontSize: 13 }}>
                以下 {totalResources} 筆資料會從來源搬到保留方：
                <ResourceList>
                  {Object.entries(preview.resources).map(([k, v]) => (
                    <ResourceItem key={k}>
                      <span>{labelOf(k)}</span>
                      <strong>{v}</strong>
                    </ResourceItem>
                  ))}
                </ResourceList>
              </div>
            }
          </Hint>

          <SectionTitle>欄位選擇</SectionTitle>
          <Text type='secondary' style={{ fontSize: 12 }}>
            點擊欄位選擇保留哪一邊（預設「保留方有值就用保留方，空才補來源」）
          </Text>

          <CompareRow style={{ background: '#fafafa', fontWeight: 600 }}>
            <div></div>
            <div>保留（target）：{preview.target.name}</div>
            <div>來源（source）：{preview.source.name}</div>
          </CompareRow>

          {FIELDS.map(({ key, label, format }) => {
            const tv = format ? format(preview.target[key]) : preview.target[key]
            const sv = format ? format(preview.source[key]) : preview.source[key]
            const choice = fieldChoices[key] || (tv ? 'target' : 'source')
            return (
              <CompareRow key={key}>
                <FieldLabel>{label}</FieldLabel>
                <ValueCell
                  $selected={choice === 'target'}
                  onClick={() =>
                    setFieldChoices({ ...fieldChoices, [key]: 'target' })
                  }
                >
                  {tv || <Text type='secondary'>(空)</Text>}
                </ValueCell>
                <ValueCell
                  $selected={choice === 'source'}
                  onClick={() =>
                    setFieldChoices({ ...fieldChoices, [key]: 'source' })
                  }
                >
                  {sv || <Text type='secondary'>(空)</Text>}
                </ValueCell>
              </CompareRow>
            )
          })}

          <SectionTitle>本店欄位</SectionTitle>

          {BUSINESS_FIELDS.map(({ key, label, read }) => {
            const tv = read(preview.target.businessSpecific)
            const sv = read(preview.source.businessSpecific)
            const choice = fieldChoices[key] || (tv ? 'target' : 'source')
            return (
              <CompareRow key={key}>
                <FieldLabel>{label}</FieldLabel>
                <ValueCell
                  $selected={choice === 'target'}
                  onClick={() =>
                    setFieldChoices({ ...fieldChoices, [key]: 'target' })
                  }
                >
                  {tv || <Text type='secondary'>(空)</Text>}
                </ValueCell>
                <ValueCell
                  $selected={choice === 'source'}
                  onClick={() =>
                    setFieldChoices({ ...fieldChoices, [key]: 'source' })
                  }
                >
                  {sv || <Text type='secondary'>(空)</Text>}
                </ValueCell>
              </CompareRow>
            )
          })}

          <Divider />

          <Hint
            tone='info'
            title='以下欄位會自動處理'
          >
            {
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
                <li>備註：兩邊串接（含分隔線）</li>
                <li>標籤：聯集</li>
                <li>消費總額、紅利點數：加總</li>
                <li>最後到店：取較晚那次</li>
                <li>客戶照片：合併</li>
                <li>LINE 配對（userId / platformData）：保留方為空就用來源</li>
              </ul>
            }
          </Hint>
        </>
      )}
    </Modal>
  )
}

const RESOURCE_LABELS = {
  bookings: '預約',
  orders: '結帳訂單',
  balances: '儲值帳戶',
  classTickets: '堂票',
  ticketUsages: '堂票使用',
  valueAddedPurchases: '加值購買',
  inventoryUsages: '庫存使用',
  customerGroups: '群組成員'
}
const labelOf = (k) => RESOURCE_LABELS[k] || k

export default CustomerMergeModal
